import type { AccessType } from '@/features/instructions/types'
import type { ArticleForm } from '@/features/instructions/lib/articleDraft'
import type { EditorTabId } from './EditorRail'
import { ContentPanel } from './panels/ContentPanel'
import { CoverTab } from './tabs/CoverTab'
import { AccessTab } from './tabs/AccessTab'
import { Separator } from '@teach/ui'
import { DeleteSection } from './tabs/details/DeleteSection'
import { useArticleAccess } from './useArticleAccess'
import { ArticleChangesSection } from './ArticleChangesSection'

interface Props {
  activeTab: EditorTabId
  activeLang: 'ru' | 'en'
  form: ArticleForm
  update: <K extends keyof ArticleForm>(key: K, val: ArticleForm[K]) => void
  /** `null` — статья ещё не создана. */
  articleId: number | null
  /** Живое поле статьи — только фолбэк до ответа модерации: доступ не поле формы. */
  accessType: AccessType
  dirty: boolean
  /** Статья на проверке: поля формы `disabled`. */
  frozen: boolean
  slug: string | null
  isCreate: boolean
  onDelete: () => void
  authorName?: string | null
  authorAvatar?: string | null
  viewsCount?: number
}

export const EditorTabContent = ({
  activeTab,
  activeLang,
  form,
  update,
  articleId,
  accessType,
  dirty,
  frozen,
  slug,
  isCreate,
  onDelete,
  authorName,
  authorAvatar,
  viewsCount,
}: Props) => {
  // Хук здесь, а не во вкладке: отсчёт `429` переживает переключение вкладок.
  const articleAccess = useArticleAccess(articleId)
  const access = articleAccess.access ?? accessType
  if (activeTab === 'content') {
    return <ContentPanel form={form} update={update} activeLang={activeLang} disabled={frozen} />
  }
  if (activeTab === 'cover') {
    const titleForPreview = activeLang === 'en' ? (form.titleEn ?? '') : form.title
    return (
      <CoverTab
        title={titleForPreview}
        coverType={form.coverType ?? 'type1'}
        onCoverTypeChange={(t) => update('coverType', t)}
        gradient={form.gradient}
        onGradientChange={(g) => update('gradient', g)}
        cardImage={form.cardImage}
        onCardImageChange={(url) => update('cardImage', url)}
        titleColor={form.titleColor ?? null}
        onTitleColorChange={(c) => update('titleColor', c)}
        authorName={authorName}
        authorAvatar={authorAvatar}
        viewsCount={viewsCount}
        disabled={frozen}
      />
    )
  }
  return (
    <div className="space-y-6">
      <ArticleChangesSection articleId={articleId} dirty={dirty} frozen={frozen} />
      <AccessTab
        accessType={access}
        articleAccess={articleAccess}
        dirty={dirty}
        frozen={frozen}
        isCreate={isCreate}
        slug={slug}
        activeLang={activeLang}
        categoryId={form.categoryId ?? null}
        onCategoryIdChange={(id) => update('categoryId', id)}
      />
      {isCreate ? null : (
        <>
          <Separator className="my-4" />
          <DeleteSection onDelete={onDelete} instructionTitle={form.title} />
        </>
      )}
    </div>
  )
}
